import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader, Panel } from '../components/Page';
import { Chip } from '../components/Chips';
import { CardSkeleton, ErrorState } from '../components/Feedback';
import { ConfirmDialog } from '../components/Overlay';
import { assistant } from '../lib/endpoints';
import { useAsync } from '../lib/hooks';
import { errorMessage } from '../lib/api';
import type { AssistantActionDto, AssistantCheckDto, AssistantFactDto, AssistantReplyDto } from '../lib/types';

const QUESTION_MAX = 1000;

type Entry =
  | { kind: 'user'; id: string; text: string }
  | { kind: 'reply'; id: string; reply: AssistantReplyDto }
  | { kind: 'note'; id: string; text: string };

function modeLabel(mode: string): string {
  switch (mode) {
    case 'ai-reference-selection':
      return 'AI-selected, approved product references';
    case 'local-reference-search':
      return 'Local reference matches — not an AI answer';
    default:
      return 'Local setup guidance';
  }
}

function checkTone(status: string): 'good' | 'warn' | 'bad' | 'neutral' {
  switch (status) {
    case 'complete':
    case 'enabled':
      return 'good';
    case 'disabled':
      return 'bad';
    case 'not-required':
      return 'neutral';
    default:
      return 'warn';
  }
}

function FactCard({ fact, actions, onOpen }: { fact: AssistantFactDto; actions: Map<string, AssistantActionDto>; onOpen: (route: string) => void }): JSX.Element {
  const action = fact.action ? actions.get(fact.action) : undefined;
  return (
    <article className="assistant-fact">
      <h4>{fact.title}</h4>
      <p>{fact.text}</p>
      <div className="assistant-fact-foot">
        <details>
          <summary>Product source</summary>
          <small className="source">{fact.source}</small>
        </details>
        {action ? (
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => onOpen(action.route)}>
            {action.caption}
          </button>
        ) : null}
      </div>
    </article>
  );
}

function CheckRow({ check, actions, onOpen }: { check: AssistantCheckDto; actions: Map<string, AssistantActionDto>; onOpen: (route: string) => void }): JSX.Element {
  const action = check.action ? actions.get(check.action) : undefined;
  return (
    <li className="assistant-check">
      <div className="assistant-check-head">
        <span className="cell-strong">{check.title}</span>
        <Chip tone={checkTone(check.status)}>{check.status}</Chip>
      </div>
      {check.detail ? <p className="muted">{check.detail}</p> : null}
      {action ? (
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => onOpen(action.route)}>
          {action.caption}
        </button>
      ) : null}
    </li>
  );
}

/**
 * THE iERP SETUP ASSISTANT.
 *
 * The same product Business Central ships in the MultiEntity ToolKit, hosted by the gateway:
 * iERP AI selects approved product references for a question; the gateway renders those
 * references, its own live setup checks and the actions that open the right page. iERP AI writes
 * no text a person reads and receives no setup values, credentials or QuickBooks data — the consent
 * says exactly what leaves, once per browser session, before the first AI question. "Per browser
 * session" means sessionStorage: leaving the page and coming back does not ask again, closing the
 * tab does, and a change to the consent text asks again because the text itself is what was
 * agreed to.
 *
 * Everything on this page stays useful without an AI call: the pending setup list is live gateway
 * state, and a question that cannot go to iERP AI (not registered, no licence, allowance used up,
 * iERP AI unreachable) gets local reference matches and says so plainly in the mode label.
 */
/** Where a granted consent lives for the rest of the browser session. */
const CONSENT_KEY = 'ierp.assistant.consent';

export function AssistantPage(): JSX.Element {
  const navigate = useNavigate();
  const context = useAsync((signal) => assistant.context(signal), []);

  const [question, setQuestion] = useState('');
  const [busy, setBusy] = useState(false);
  const [entries, setEntries] = useState<Entry[]>([]);
  const [consented, setConsented] = useState(false);
  const [consentOpen, setConsentOpen] = useState(false);
  const consentText = context.data?.consent ?? '';

  // The question the consent dialog is standing in front of. It is held here rather than re-read
  // from state after the dialog closes: setConsented is asynchronous, so a submit that ran again
  // after Continue would still see the render's stale `consented === false` and re-open the dialog,
  // which is how one question could cost two clicks.
  const [pendingQuestion, setPendingQuestion] = useState('');
  const [previousFactIds, setPreviousFactIds] = useState<string[]>([]);
  const timelineRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    timelineRef.current?.scrollTo({ top: timelineRef.current.scrollHeight });
  }, [entries]);

  // The consent a person already gave in THIS browser session stands for this page's whole
  // lifetime, including a remount. It is stored against the consent text, so a changed consent
  // asks again rather than inheriting agreement to different words. sessionStorage can be absent
  // or refused (private windows, blocked site data); when it is, the consent is simply asked for
  // again, which is the safe direction.
  useEffect(() => {
    if (!consentText) return;

    try {
      setConsented(window.sessionStorage.getItem(CONSENT_KEY) === consentText);
    } catch {
      setConsented(false);
    }
  }, [consentText]);

  const actions = new Map<string, AssistantActionDto>((context.data?.actions ?? []).map((a) => [a.id, a]));
  const pending = (context.data?.checks ?? []).filter((c) => c.pending).sort((a, b) => a.sequence - b.sequence);

  const open = (route: string) => {
    const [path, hash] = route.split('#');
    navigate(hash ? `${path}#${hash}` : path);
  };

  const submit = async () => {
    const text = question.trim();
    if (!text || busy) return;

    // THE CONSENT GATE, and the only place it is applied. It is per browser session, and it only
    // stands between a person and an AI call: a tenant that cannot ask iERP AI anyway gets local
    // help without being asked to consent to something that will not happen.
    if (context.data?.canAskAi && !consented) {
      setPendingQuestion(text);
      setConsentOpen(true);
      return;
    }

    await sendApproved(text);
  };

  /// Sends a question that has passed the gate. Nothing here re-reads the consent state, so a
  /// confirmation and a send in the same event cannot disagree about whether consent was given.
  const sendApproved = async (text: string) => {
    if (!text || busy) return;

    setBusy(true);
    const id = crypto.randomUUID();
    setEntries((current) => [...current.slice(-59), { kind: 'user', id, text }]);
    setQuestion('');

    try {
      const reply = await assistant.ask({ question: text, previousFactIds });
      setEntries((current) => [...current.slice(-59), { kind: 'reply', id: reply.requestId, reply }]);
      setPreviousFactIds(reply.facts.slice(0, 4).map((f) => f.id));
      if (reply.kind === 'status' || reply.kind === 'guide') context.reload();
    } catch (caught) {
      setEntries((current) => [...current.slice(-59), { kind: 'note', id: crypto.randomUUID(), text: errorMessage(caught) }]);
    } finally {
      setBusy(false);
    }
  };

  const onKey = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void submit();
    }
  };

  return (
    <>
      <PageHeader
        title="Assistant"
        description="Ask how to set up or use the gateway. iERP AI selects approved product references; the gateway adds its own live checks and the page to open."
        actions={
          <button type="button" className="btn btn-secondary btn-sm" onClick={context.reload}>
            Refresh setup
          </button>
        }
      />

      <div className="assistant-layout">
        <Panel className="assistant-main" bodyClassName="assistant-main-body">
          {context.loading && !context.data ? (
            <CardSkeleton count={2} />
          ) : context.error ? (
            <ErrorState message="The assistant could not load." detail={context.error} onRetry={context.reload} />
          ) : (
            <>
              <div className="assistant-status">
                {context.data?.canAskAi ? (
                  <Chip tone="good">iERP AI available</Chip>
                ) : (
                  <Chip tone="warn">Local reference help</Chip>
                )}
                {!context.data?.canAskAi && context.data?.canAskAiReason ? (
                  <span className="muted">{context.data.canAskAiReason}</span>
                ) : null}
                <span className="muted assistant-revision">catalogue {context.data?.knowledgeRevision}</span>
              </div>

              <div className="assistant-timeline" ref={timelineRef} aria-live="polite">
                {entries.length === 0 ? (
                  <p className="muted assistant-empty">
                    Try “How do I connect QuickBooks?”, “Why is a tax code line blocked?”, or “Walk me through setting up the gateway.”
                  </p>
                ) : null}
                {entries.map((entry) => {
                  if (entry.kind === 'user') {
                    return (
                      <div key={entry.id} className="assistant-msg assistant-msg-user">
                        <p>{entry.text}</p>
                      </div>
                    );
                  }
                  if (entry.kind === 'note') {
                    return (
                      <div key={entry.id} className="assistant-msg assistant-msg-note">
                        <p>{entry.text}</p>
                      </div>
                    );
                  }
                  const { reply } = entry;
                  return (
                    <div key={entry.id} className="assistant-msg assistant-msg-reply">
                      <div className="assistant-mode">
                        <Chip tone={reply.mode === 'ai-reference-selection' ? 'info' : 'neutral'}>{modeLabel(reply.mode)}</Chip>
                        <span className="muted">{reply.kind}</span>
                      </div>
                      <p>{reply.message}</p>
                      {reply.facts.map((fact) => (
                        <FactCard key={fact.id} fact={fact} actions={actions} onOpen={open} />
                      ))}
                      {reply.checks.length > 0 ? (
                        <ul className="assistant-checks">
                          {reply.checks.map((check) => (
                            <CheckRow key={check.id} check={check} actions={actions} onOpen={open} />
                          ))}
                        </ul>
                      ) : null}
                      {reply.actions.length > 0 && reply.facts.length === 0 ? (
                        <div className="assistant-actions">
                          {reply.actions.map((a) => (
                            <button key={a.id} type="button" className="btn btn-ghost btn-sm" onClick={() => open(a.route)}>
                              {a.caption}
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
                {busy ? <p className="muted">Asking…</p> : null}
              </div>

              <div className="assistant-composer">
                <textarea
                  className="input textarea"
                  rows={3}
                  maxLength={QUESTION_MAX}
                  placeholder="Ask about setup, licensing, QuickBooks, documents or trading partners…"
                  value={question}
                  disabled={busy}
                  onChange={(event) => setQuestion(event.target.value)}
                  onKeyDown={onKey}
                  aria-label="Your question"
                />
                <div className="assistant-composer-foot">
                  <small className="muted">
                    Questions only. Do not enter credentials, bank details or personal data. Enter sends; Shift+Enter adds a line. {question.length}/{QUESTION_MAX}
                  </small>
                  <button type="button" className="btn btn-primary" disabled={busy || question.trim().length === 0} onClick={() => void submit()}>
                    Ask
                  </button>
                </div>
              </div>
            </>
          )}
        </Panel>

        <Panel title={`Pending setup (${pending.length === 0 ? 'none' : pending.length})`} className="assistant-side">
          {context.data ? (
            pending.length === 0 ? (
              <p className="muted">Every check the gateway knows about is complete.</p>
            ) : (
              <ul className="assistant-checks">
                {pending.map((check) => (
                  <CheckRow key={check.id} check={check} actions={actions} onOpen={open} />
                ))}
              </ul>
            )
          ) : null}
          {context.data ? (
            <details className="assistant-all-checks">
              <summary>All checks ({context.data.checks.length})</summary>
              <ul className="assistant-checks">
                {[...context.data.checks].sort((a, b) => a.sequence - b.sequence).map((check) => (
                  <CheckRow key={check.id} check={check} actions={actions} onOpen={open} />
                ))}
              </ul>
            </details>
          ) : null}
        </Panel>
      </div>

      <ConfirmDialog
        open={consentOpen}
        title="Process this question with iERP AI?"
        message={consentText}
        confirmLabel="Continue for this session"
        onConfirm={() => {
          setConsented(true);
          setConsentOpen(false);

          try {
            window.sessionStorage.setItem(CONSENT_KEY, consentText);
          } catch {
            // No session storage: the consent stands for this mount and is asked for again later.
          }

          // The approved request goes straight out. Calling submit() here would run the gate again
          // against a `consented` value this render still reads as false.
          const approved = pendingQuestion;
          setPendingQuestion('');
          void sendApproved(approved);
        }}
        onCancel={() => {
          setConsentOpen(false);
          setPendingQuestion('');
        }}
      />
    </>
  );
}
